import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { usesFillHeader } from '../../lib/appChrome'
import { useDisplayStore } from '../../context/DisplayStoreProvider'
import SettingsModal from '../modals/SettingsModal'
import { useTheme } from '../../context/ThemeProvider'
import { useWalletAuth } from '../../context/WalletAuthProvider'
import { fetchRecentTrades } from '../../lib/lighterApi'
import { MORE_LINKS, TOOL_LINKS } from '../../lib/navLinks'
import MobileSidebar from './MobileSidebar'
import '../modals/SettingsModal.css'
import './header-mobile.css'

function truncateAddress(address) {
  if (!address) return ''
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

const NAV_ACTIVE =
  'flex h-[1.75rem] shrink-0 items-center justify-center rounded-card px-2.5 text-md/sm transition-colors bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] font-medium text-ink'
const NAV_IDLE =
  'flex h-[1.75rem] shrink-0 items-center justify-center rounded-card px-2.5 text-md/sm transition-colors cursor-pointer font-normal text-ink/80 hover:bg-ink/5 hover:text-ink'
const ICON_BTN =
  "group relative flex size-[1.75rem] shrink-0 items-center justify-center rounded-pill max-mobile:min-h-0 max-mobile:overflow-visible max-mobile:before:absolute max-mobile:before:-inset-[2px] max-mobile:before:content-[''] bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] overflow-hidden text-ink/80 transition-[background-color,color,transform] hover:bg-ink/10 hover:text-ink light:hover:bg-surface"
const MENU_PANEL =
  'header-menu z-[1000] min-w-[220px] overflow-hidden rounded-card border-[0.5px] border-line bg-surface p-1 shadow-[0_12px_40px_rgba(0,0,0,0.45)]'
const MENU_ITEM =
  'flex w-full items-center rounded-card px-2.5 py-1.5 text-left text-sm text-ink/80 transition-colors hover:bg-ink/5 hover:text-ink'
const LANGUAGES = [
  { id: 'en', label: 'English' },
  { id: 'zh', label: '普通话' },
  { id: 'ja', label: '日本語' },
  { id: 'tr', label: 'Türkçe' },
  { id: 'es', label: 'Español' },
  { id: 'ru', label: 'Русский' },
]
const DATE_FORMATS = ['MM/DD/YYYY', 'DD/MM/YYYY']
const SETTINGS_KEY = 'lighter:user-settings'

let globalBlockHeight = '—'
const blockHeightSubscribers = new Set()

function setGlobalBlockHeight(height) {
  globalBlockHeight = height
  for (const sub of blockHeightSubscribers) {
    sub(height)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('lighter:block-height', (event) => {
    const height = event.detail?.height
    if (height) {
      setGlobalBlockHeight(Number(height).toLocaleString('en-US'))
    }
  })

  setInterval(async () => {
    if (blockHeightSubscribers.size === 0) return
    if (window.location.pathname.startsWith('/trade')) return
    
    try {
      const trades = await fetchRecentTrades(0, 1)
      if (trades && trades[0]?.blockHeight) {
        setGlobalBlockHeight(Number(trades[0].blockHeight).toLocaleString('en-US'))
      }
    } catch (e) {
      // Ignore fetch errors
    }
  }, 4000)
}

const DEFAULT_SETTINGS = {
  confirmOrders: true,
  maxSlippage: 1,
  orderBookDepth: 14,
  language: 'en',
  dateFormat: 'MM/DD/YYYY',
  setSizeOnClick: false,
  orderbookAnimation: false,
  askBidRatio: false,
  marketNews: false,
  orderFilledSound: false,
  fillSound: 'Neutral',
  backgroundFillNotification: false,
  funMode: false,
  altDepositFlow: false,
  notificationPlacement: 'bottom-right',
  showFooter: true,
}

function loadSettings() {
  try {
    return {
      ...DEFAULT_SETTINGS,
      ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') || {}),
    }
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

function saveSettings(next) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(next))
  window.dispatchEvent(
    new CustomEvent('lighter:settings', { detail: next })
  )
}

function NavMenuLink({ item, onSelect, stacked = false }) {
  const content = stacked ? (
    <>
      <span className="text-md/sm text-ink/85 transition-colors group-hover:text-ink">{item.label}</span>
      {item.description ? <span className="text-2xs/xs text-muted">{item.description}</span> : null}
    </>
  ) : (
    <div className="flex flex-col items-start gap-0.5">
      <span className="text-sm font-medium text-ink/80 transition-colors group-hover:text-ink">{item.label}</span>
      {item.description && (
        <span className="text-xs text-ink/50 transition-colors group-hover:text-ink/70">{item.description}</span>
      )}
    </div>
  )

  const className = stacked
    ? 'group flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-pill px-2.5 py-2 text-left outline-none transition-colors hover:bg-ink/6'
    : 'group flex w-full items-center rounded-card px-2.5 py-2 text-left transition-colors hover:bg-ink/5'

  if (item.type === 'button') {
    return (
      <button
        type="button"
        data-testid={item.testId}
        className={className}
        role={stacked ? 'menuitem' : undefined}
        onClick={() => {
          if (item.label === 'Calculator') {
            window.dispatchEvent(new Event('lighter:open-calculator'))
          }
          if (item.label === 'Support & Community') {
            window.open('https://discord.gg/lighter', '_blank', 'noopener,noreferrer')
          }
          onSelect?.()
        }}
      >
        {content}
      </button>
    )
  }
  const external = item.external
  const shared = {
    'data-testid': item.testId,
    className,
    role: stacked ? 'menuitem' : undefined,
    onClick: () => onSelect?.(),
  }
  if (external) {
    return (
      <a {...shared} href={item.href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    )
  }
  return (
    <Link {...shared} to={item.href} data-discover="true">
      {content}
    </Link>
  )
}

function DropdownChevron({ open }) {
  return (
    <span
      data-icon="true"
      className={`header-dropdown-chevron flex shrink-0 items-center justify-center size-3 text-ink/50 transition-[rotate] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]${open ? ' is-open' : ' group-hover:rotate-180'}`}
    >
      <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-full">
        <path
          d={open ? 'M3 7.25 6 4.5l3 2.75' : 'M3 4.75 6 7.5l3-2.75'}
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

/** Portaled menu so overflow:hidden ancestors cannot clip it. */
function HeaderMenuPortal({
  open,
  anchorRef,
  align = 'left',
  width,
  minWidth,
  height,
  labelledBy,
  role = 'menu',
  menuClassName = '',
  menuKind = 'true',
  children,
}) {
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return undefined
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect()
      const menuWidth = width || minWidth || 220
      let left = align === 'right' ? rect.right - menuWidth : rect.left
      left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8))
      setPos({ top: rect.bottom + 6, left })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, anchorRef, align, width, minWidth])

  if (!open) return null
  return createPortal(
    <div
      role={role}
      aria-labelledby={labelledBy}
      className={`${MENU_PANEL}${menuClassName ? ` ${menuClassName}` : ''}`}
      data-header-menu={menuKind}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: width || undefined,
        minWidth: minWidth ?? width ?? 220,
        height: height || undefined,
      }}
    >
      {children}
    </div>,
    document.body
  )
}

function formatSampleDate(pattern) {
  const now = new Date()
  const month = now.getMonth() + 1
  const day = now.getDate()
  const year = now.getFullYear()
  const md = `${month}/${day}/${year}`
  const dm = `${day}/${month}/${year}`
  return pattern === 'DD/MM/YYYY' ? `${dm} - DD/MM/YYYY` : `${md} - MM/DD/YYYY`
}

function MenuCheck() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-3.5 shrink-0 text-ink" aria-hidden="true">
      <path
        d="M3.5 8.2 6.4 11.1 12.5 4.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function LanguageGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="size-full" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10.667 7.5a.5.5 0 0 0-1 0v.833H7.5a.5.5 0 0 0 0 1h.876a4.783 4.783 0 0 0 1.038 2.316 5.03 5.03 0 0 1-.255.152 4.75 4.75 0 0 1-1.694.533.5.5 0 1 0 .069.998c.052-.004.104-.009.195-.021.115-.016.277-.043.471-.088a5.939 5.939 0 0 0 1.441-.545c.173-.095.347-.204.517-.328.357.268.756.486 1.188.643a.5.5 0 0 0 .34-.941 4.13 4.13 0 0 1-.774-.392 4.832 4.832 0 0 0 1.035-2.327h.886a.5.5 0 0 0 0-1h-2.166V7.5Z"
        fill="currentColor"
        fillOpacity="0.4"
      />
      <path
        fillRule="evenodd"
        d="M14.168 10.667a.5.5 0 0 0-.468.324l-2 5.334a.5.5 0 1 0 .936.351l.504-1.343h2.39l.503 1.343a.5.5 0 1 0 .936-.351l-2-5.334a.5.5 0 0 0-.468-.324h-.333Zm.987 3.666-.82-2.187-.82 2.187h1.64Z"
        fill="currentColor"
      />
    </svg>
  )
}

function CalendarGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="size-full" aria-hidden="true">
      <path
        opacity="0.4"
        fillRule="evenodd"
        d="M11.667 15.333a3.67 3.67 0 0 1 5.333-3.262V9.667H7v5.5C7 16.179 7.82 17 8.833 17h3.239a3.65 3.65 0 0 1-.405-1.667Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        d="M10.333 6.833a.5.5 0 0 0-1 0v.834h-.5A1.833 1.833 0 0 0 7 9.5v.833h10V9.5a1.833 1.833 0 0 0-1.833-1.833h-.5v-.834a.5.5 0 0 0-1 0v.834h-3.334v-.834Z"
        fill="currentColor"
      />
    </svg>
  )
}

export default function Header() {
  const { pathname } = useLocation()
  const { openConnectModal } = useDisplayStore()
  const { isConnected, userAddress, logout } = useWalletAuth()
  const { theme, toggleTheme } = useTheme()
  const isLight = theme === 'light'
  const [menuOpen, setMenuOpen] = useState(false)
  const [openMenu, setOpenMenu] = useState(null)
  const [blockHeight, setBlockHeight] = useState(globalBlockHeight)
  const [settings, setSettings] = useState(loadSettings)
  const rootRef = useRef(null)
  const toolsBtnRef = useRef(null)
  const moreBtnRef = useRef(null)
  const settingsBtnRef = useRef(null)
  const langBtnRef = useRef(null)
  const toolsId = useId()
  const moreId = useId()
  const settingsId = useId()
  const langId = useId()

  const closeMenu = useCallback(() => setMenuOpen(false), [])
  const openMobileMenu = useCallback(() => setMenuOpen(true), [])
  const closeDropdowns = useCallback(() => {
    setOpenMenu(null)
  }, [])

  const isTrade = pathname.startsWith('/trade')
  const isPortfolio = pathname.startsWith('/portfolio')
  const isPublicPools = pathname.startsWith('/public-pools')
  const isStaking = pathname.startsWith('/staking')
  const isReferrals = pathname.startsWith('/referrals')
  const isSubAccounts = pathname.startsWith('/sub-accounts')
  const isFundingRisk = pathname.startsWith('/funding-market-risk-parameters')
  const fillHeader = usesFillHeader(pathname)
  const keepsSafeAreaSpacer = isFundingRisk || isSubAccounts

  useEffect(() => {
    blockHeightSubscribers.add(setBlockHeight)
    return () => blockHeightSubscribers.delete(setBlockHeight)
  }, [])

  useEffect(() => {
    const onDoc = (event) => {
      const t = event.target
      if (rootRef.current?.contains(t)) return
      if (t?.closest?.('[data-header-menu]')) return
      if (t?.closest?.('[data-settings-root="true"]')) return
      closeDropdowns()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') closeDropdowns()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [closeDropdowns])

  const toggle = (name) => {
    setOpenMenu((prev) => (prev === name ? null : name))
  }

  const patchSettings = (partial) => {
    setSettings((prev) => {
      const next = { ...prev, ...partial }
      saveSettings(next)
      return next
    })
  }

  const formattedBlock = blockHeight

  return (
    <div className="relative" ref={rootRef}>
      <div className="hidden" data-testid="account-NoWallet"></div>
      <div
        className={`sticky top-0 z-20 h-[env(safe-area-inset-top)] w-full bg-bg max-mobile:z-10${fillHeader && !keepsSafeAreaSpacer ? ' hidden' : ''}`}
        aria-hidden="true"
      ></div>
      <div
        className={
          fillHeader
            ? `sticky z-20 flex w-full items-center justify-between px-3 pt-3 pb-2.5 max-mobile:z-10 border-b-[0.5px] border-line bg-fill backdrop-blur-md backdrop-saturate-150 light:bg-bg/80 ${keepsSafeAreaSpacer ? 'top-[env(safe-area-inset-top)]' : 'top-0'}`
            : 'sticky z-20 flex w-full items-center justify-between px-3 pt-3 pb-2.5 max-mobile:z-10 top-[env(safe-area-inset-top)]'
        }
      >
        <div className="flex items-center gap-2">
          <Link
            className="flex items-center gap-1.5 text-ink"
            to="/trade/LIT"
            data-discover="true"
          >
            <span
              data-icon="true"
              className="flex items-center justify-center aspect-wordmark h-[20px] shrink-0 text-ink"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 840 253"
                aria-label="Lighter"
                className="size-full"
              >
                <path d="M53.8113 197.302L0 248V53.4414L53.8113 0V197.302ZM124 197.423L70.1887 248V172.122L124 118.833V197.423Z"></path>
                <path
                  d="M779.82 205.734V90.234H801.914V108.938H802.352C804.466 102.594 807.784 97.672 812.305 94.172 816.898 90.599 822.878 88.813 830.242 88.813 831.992 88.813 833.596 88.885 835.055 89.031 836.586 89.104 837.789 89.214 838.664 89.359V110.906C837.789 110.688 836.221 110.469 833.961 110.25 831.701 109.958 829.221 109.812 826.523 109.812 822.221 109.812 818.247 110.797 814.602 112.766 811.029 114.734 808.185 117.76 806.07 121.844 803.956 125.927 802.898 131.141 802.898 137.484V205.734H779.82ZM716.047 208.359C704.599 208.359 694.755 205.771 686.516 200.594 678.349 195.417 672.042 188.307 667.594 179.266 663.219 170.151 661.031 159.797 661.031 148.203 661.031 136.391 663.328 125.964 667.922 116.922 672.516 107.807 678.859 100.661 686.953 95.484 695.047 90.234 704.344 87.609 714.844 87.609 723.01 87.609 730.375 89.068 736.938 91.984 743.573 94.901 749.26 99.057 754 104.453 758.74 109.776 762.385 116.12 764.938 123.484 767.49 130.849 768.766 139.016 768.766 147.984V154.219H671.422V136.719H756.625L746.453 142.078C746.453 134.932 745.177 128.734 742.625 123.484 740.073 118.161 736.427 114.078 731.688 111.234 727.021 108.318 721.479 106.859 715.063 106.859 708.719 106.859 703.214 108.318 698.547 111.234 693.88 114.078 690.234 118.161 687.609 123.484 685.057 128.734 683.781 134.932 683.781 142.078V152.031C683.781 159.323 685.057 165.776 687.609 171.391 690.161 176.932 693.844 181.271 698.656 184.406 703.542 187.542 709.448 189.109 716.375 189.109 721.406 189.109 725.854 188.344 729.719 186.812 733.583 185.208 736.792 183.021 739.344 180.25 741.896 177.479 743.719 174.271 744.813 170.625H767.016C765.63 178.062 762.531 184.625 757.719 190.312 752.979 195.927 747 200.339 739.781 203.547 732.635 206.755 724.724 208.359 716.047 208.359ZM657.141 90.234V109.266H590.203V90.234H657.141ZM610.547 58.734H633.625V175.219C633.625 179.448 634.5 182.438 636.25 184.188 638.073 185.865 641.281 186.703 645.875 186.703 647.625 186.703 649.557 186.703 651.672 186.703 653.786 186.703 655.609 186.703 657.141 186.703V205.734C655.245 205.734 652.948 205.734 650.25 205.734 647.625 205.734 645.073 205.734 642.594 205.734 631.729 205.734 623.672 203.474 618.422 198.953 613.172 194.359 610.547 187.432 610.547 178.172V58.734ZM508.164 139.234V205.734H485.086V42.766H508.164V118.891H502.367C506.159 108.536 511.409 100.807 518.117 95.703 524.898 90.526 533.065 87.938 542.617 87.938 550.565 87.938 557.674 89.688 563.945 93.188 570.289 96.615 575.284 101.719 578.93 108.5 582.576 115.281 584.398 123.703 584.398 133.766V205.734H561.32V136.609C561.32 127.349 559.096 120.385 554.648 115.719 550.273 110.979 544.076 108.609 536.055 108.609 530.805 108.609 526.065 109.667 521.836 111.781 517.68 113.896 514.362 117.214 511.883 121.734 509.404 126.255 508.164 132.089 508.164 139.234ZM413.164 252.984C403.685 252.984 395.336 251.562 388.117 248.719 380.971 245.948 375.211 241.865 370.836 236.469 366.461 231.146 363.763 224.729 362.742 217.219H385.82C386.841 220.938 388.591 224.036 391.07 226.516 393.622 229.068 396.758 230.964 400.477 232.203 404.268 233.516 408.497 234.172 413.164 234.172 422.497 234.172 429.789 231.656 435.039 226.625 440.289 221.594 442.914 214.411 442.914 205.078V187.578H442.805C440.18 192.026 437.044 195.708 433.398 198.625 429.826 201.542 425.815 203.729 421.367 205.188 416.992 206.573 412.253 207.266 407.148 207.266 397.159 207.266 388.372 204.75 380.789 199.719 373.279 194.615 367.445 187.615 363.289 178.719 359.133 169.75 357.055 159.396 357.055 147.656 357.055 135.917 359.133 125.562 363.289 116.594 367.518 107.552 373.388 100.516 380.898 95.484 388.409 90.453 397.122 87.938 407.039 87.938 412.07 87.938 416.81 88.63 421.258 90.016 425.779 91.401 429.862 93.589 433.508 96.578 437.227 99.495 440.362 103.25 442.914 107.844H443.023V90.234H465.773V204.203C465.773 215.87 463.404 225.276 458.664 232.422 453.997 239.568 447.654 244.781 439.633 248.062 431.685 251.344 422.862 252.984 413.164 252.984ZM411.852 187.469C418.414 187.469 424.102 185.828 428.914 182.547 433.799 179.266 437.555 174.635 440.18 168.656 442.878 162.604 444.227 155.568 444.227 147.547 444.227 139.453 442.878 132.417 440.18 126.438 437.555 120.458 433.799 115.828 428.914 112.547 424.102 109.266 418.414 107.625 411.852 107.625 405.654 107.625 400.185 109.156 395.445 112.219 390.779 115.208 387.096 119.693 384.398 125.672 381.773 131.578 380.461 138.87 380.461 147.547 380.461 156.151 381.773 163.443 384.398 169.422 387.096 175.328 390.779 179.812 395.445 182.875 400.185 185.938 405.654 187.469 411.852 187.469ZM320.242 205.734V90.234H343.32V205.734H320.242ZM331.727 70.328C327.643 70.328 324.18 68.979 321.336 66.281 318.565 63.51 317.18 60.156 317.18 56.219 317.18 52.208 318.565 48.854 321.336 46.156 324.18 43.385 327.643 42 331.727 42 335.81 42 339.273 43.385 342.117 46.156 344.961 48.854 346.383 52.208 346.383 56.219 346.383 60.156 344.961 63.51 342.117 66.281 339.273 68.979 335.81 70.328 331.727 70.328ZM207.203 205.734V42.766H231.484V184.406H308.594V205.734H207.203Z"
                  opacity="0.9"
                ></path>
              </svg>
            </span>
          </Link>
          <div
            aria-hidden="true"
            className="h-5 shrink-0 border-l-[0.5px] border-line max-mobile:hidden"
          ></div>
          <div className="flex items-center gap-1 max-mobile:hidden">
            <Link
              data-testid="navbar-trade-link"
              aria-current={isTrade ? 'page' : undefined}
              className={isTrade ? NAV_ACTIVE : NAV_IDLE}
              to="/trade/LIT"
              data-discover="true"
            >
              Trade
            </Link>
            <Link
              data-testid="navbar-portfolio-link"
              aria-current={isPortfolio ? 'page' : undefined}
              className={isPortfolio ? NAV_ACTIVE : NAV_IDLE}
              to="/portfolio"
              data-discover="true"
            >
              Portfolio
            </Link>
            <Link
              data-testid="navbar-public-pools-link"
              aria-current={isPublicPools ? 'page' : undefined}
              className={isPublicPools ? NAV_ACTIVE : NAV_IDLE}
              to="/public-pools"
              data-discover="true"
            >
              Public Pools
            </Link>
            <Link
              data-testid="navbar-staking-link"
              aria-current={isStaking ? 'page' : undefined}
              className={isStaking ? NAV_ACTIVE : NAV_IDLE}
              to="/staking"
              data-discover="true"
            >
              Staking
            </Link>
            <Link
              data-testid="navbar-referrals-link"
              aria-current={isReferrals ? 'page' : undefined}
              className={isReferrals ? NAV_ACTIVE : NAV_IDLE}
              to="/referrals"
              data-discover="true"
            >
              Referrals
            </Link>
            <div className="relative">
              <button
                ref={toolsBtnRef}
                data-testid="navbar-tools-link"
                className="inline-flex text-sm/sm font-medium disabled:opacity-50 text-link hover:opacity-80 active:opacity-60 group h-[1.75rem] cursor-pointer items-center justify-center gap-1 rounded-card px-2.5 transition-colors hover:bg-ink/5 aria-expanded:bg-ink/10 light:aria-expanded:bg-surface"
                type="button"
                id={toolsId}
                aria-haspopup="menu"
                aria-expanded={openMenu === 'tools'}
                data-state={openMenu === 'tools' ? 'open' : 'closed'}
                onClick={() => toggle('tools')}
              >
                <span className="text-md/sm font-normal text-ink/80 transition-colors group-hover:text-ink group-aria-expanded:text-ink">
                  Tools
                </span>
                <DropdownChevron open={openMenu === 'tools'} />
              </button>
              <HeaderMenuPortal
                open={openMenu === 'tools'}
                anchorRef={toolsBtnRef}
                labelledBy={toolsId}
                menuKind="tools"
                menuClassName="header-nav-menu"
                width={210.047}
                minWidth={197.12}
                height={417.624}
              >
                <p className="px-2.5 pt-1.5 pb-1 text-2xs font-medium tracking-caps text-muted uppercase">
                  Tools
                </p>
                {TOOL_LINKS.map((item) => (
                  <NavMenuLink
                    key={item.testId}
                    item={item}
                    stacked
                    onSelect={closeDropdowns}
                  />
                ))}
              </HeaderMenuPortal>
            </div>
            <div className="relative">
              <button
                ref={moreBtnRef}
                data-testid="navbar-more-link"
                className="inline-flex text-sm/sm font-medium disabled:opacity-50 text-link hover:opacity-80 active:opacity-60 group h-[1.75rem] cursor-pointer items-center justify-center gap-1 rounded-card px-2.5 transition-colors hover:bg-ink/5 aria-expanded:bg-ink/10 light:aria-expanded:bg-surface"
                type="button"
                id={moreId}
                aria-haspopup="menu"
                aria-expanded={openMenu === 'more'}
                data-state={openMenu === 'more' ? 'open' : 'closed'}
                onClick={() => toggle('more')}
              >
                <span className="text-md/sm font-normal text-ink/80 transition-colors group-hover:text-ink group-aria-expanded:text-ink">
                  More
                </span>
                <DropdownChevron open={openMenu === 'more'} />
              </button>
              <HeaderMenuPortal
                open={openMenu === 'more'}
                anchorRef={moreBtnRef}
                labelledBy={moreId}
                menuKind="more"
                menuClassName="header-nav-menu"
                width={197.12}
              >
                <p className="px-2.5 pt-1.5 pb-1 text-2xs font-medium tracking-caps text-muted uppercase">
                  More
                </p>
                {MORE_LINKS.map((item) => (
                  <NavMenuLink
                    key={item.testId}
                    item={item}
                    stacked
                    onSelect={closeDropdowns}
                  />
                ))}
              </HeaderMenuPortal>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Link
            to="/trade"
            className="group flex h-[1.75rem] shrink-0 items-center gap-1 overflow-hidden rounded-pill bg-fill px-2 shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp)] transition-colors hover:bg-ink/10 light:hover:bg-surface light:bg-surface light:shadow-[inset_0_0_0_0.5px_var(--ring-crisp)] max-mobile:hidden"
            data-discover="true"
            title="View on explorer"
          >
            <div className="h-4 w-1 shrink-0 rounded-seam bg-link"></div>
            <span className="font-mono label-meta text-ink/80 transition-colors group-hover:text-ink">
              Block
            </span>
            <span className="font-mono label-meta text-link tabular-nums">
              {formattedBlock}
            </span>
          </Link>
          <div className="relative shrink-0">
            <button
              ref={langBtnRef}
              type="button"
              id={langId}
              aria-haspopup="menu"
              aria-expanded={openMenu === 'lang'}
              data-state={openMenu === 'lang' ? 'open' : 'closed'}
              data-testid="localization-picker-trigger-navbar"
              aria-label="Select language and date format"
              onClick={() => toggle('lang')}
              className="group text-sm/sm disabled:opacity-50 gap-2 rounded-pill p-2 border-line disabled:border-line data-[state=open]:border-line active:bg-ink/8 disabled:bg-fill data-[state=open]:bg-ink/8 light:active:bg-surface light:disabled:bg-surface light:data-[state=open]:bg-surface group relative flex size-[1.75rem] shrink-0 items-center justify-center rounded-pill max-mobile:min-h-0 max-mobile:overflow-visible max-mobile:before:absolute max-mobile:before:-inset-[2px] max-mobile:before:content-[''] bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] overflow-visible text-ink/80 transition-[background-color,color,transform] hover:bg-ink/10 hover:text-ink light:hover:bg-surface border-0"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex size-4 items-center justify-center transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110">
                  <span data-icon="true" className="flex shrink-0 items-center justify-center size-4">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" className="size-full">
                      <g clipPath="url(#clip0_lang)">
                        <path d="M15 13.5L11.5 6.5L8 13.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M9 11.5H14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M6 2V3.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M2 3.5H10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M8 3.5C8 5.0913 7.36786 6.61742 6.24264 7.74264C5.11742 8.86786 3.5913 9.5 2 9.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M4.34192 5.5C4.75558 6.67001 5.52186 7.68297 6.53519 8.39935C7.54853 9.11572 8.75906 9.50026 10 9.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                      </g>
                      <defs>
                        <clipPath id="clip0_lang">
                          <rect width="16" height="16" fill="white"></rect>
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                </span>
              </div>
            </button>
            <HeaderMenuPortal
              open={openMenu === 'lang'}
              anchorRef={langBtnRef}
              align="right"
              width={280}
              labelledBy={langId}
            >
              <div className="flex items-center justify-between gap-2 px-2.5 pt-2 pb-1">
                <span className="settings-section-heading">Language</span>
                <span className="flex size-[24px] shrink-0 items-center justify-center text-ink">
                  <LanguageGlyph />
                </span>
              </div>
              {LANGUAGES.map((lang) => {
                const selected = settings.language === lang.id
                return (
                  <button
                    key={lang.id}
                    type="button"
                    className={`settings-picker-option flex w-full items-center justify-between px-2.5 py-1.5 text-left text-sm/sm transition-colors hover:bg-ink/5 ${
                      selected ? 'font-medium' : ''
                    }`}
                    aria-pressed={selected}
                    onClick={() => {
                      patchSettings({ language: lang.id })
                    }}
                  >
                    <span className="settings-native-label">{lang.label}</span>
                    {selected ? <MenuCheck /> : null}
                  </button>
                )
              })}
              <div className="mx-1 my-1 h-px bg-line" />
              <div className="flex items-center justify-between gap-2 px-2.5 pt-1.5 pb-1">
                <span className="settings-section-heading">Date Formatting</span>
                <span className="flex size-[24px] shrink-0 items-center justify-center text-ink">
                  <CalendarGlyph />
                </span>
              </div>
              {DATE_FORMATS.map((pattern) => {
                const selected = (settings.dateFormat || 'MM/DD/YYYY') === pattern
                return (
                  <button
                    key={pattern}
                    type="button"
                    className={`settings-picker-option flex w-full items-center justify-between px-2.5 py-1.5 text-left text-sm/sm transition-colors hover:bg-ink/5 ${
                      selected ? 'font-medium' : ''
                    }`}
                    aria-pressed={selected}
                    onClick={() => {
                      patchSettings({ dateFormat: pattern })
                    }}
                  >
                    <span className="settings-native-label">{formatSampleDate(pattern)}</span>
                    {selected ? <MenuCheck /> : null}
                  </button>
                )
              })}
            </HeaderMenuPortal>
          </div>
          <button
            className="gap-2 text-sm/sm font-medium disabled:opacity-50 hover:opacity-80 active:opacity-60 group relative flex size-[1.75rem] shrink-0 items-center justify-center rounded-pill max-mobile:min-h-0 max-mobile:overflow-visible max-mobile:before:absolute max-mobile:before:-inset-[2px] max-mobile:before:content-[''] bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] overflow-hidden text-ink/80 transition-[background-color,color,transform] hover:bg-ink/10 hover:text-ink light:hover:bg-surface"
            type="button"
            data-testid="theme-toggle"
            aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
            onClick={toggleTheme}
          >
            <span
              data-icon="true"
              className="shrink-0 inline-flex items-center justify-center transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] size-4 group-hover:rotate-45"
            >
              {isLight ? (
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-full">
                  <path
                    fill="currentColor"
                    d="M8 11.25A3.25 3.25 0 1 1 8 4.75a3.25 3.25 0 0 1 0 6.5ZM8 1.5a.75.75 0 0 1 .75.75v1.1a.75.75 0 0 1-1.5 0V2.25A.75.75 0 0 1 8 1.5Zm0 11.15a.75.75 0 0 1 .75.75v1.1a.75.75 0 0 1-1.5 0v-1.1a.75.75 0 0 1 .75-.75Zm6.5-5.4a.75.75 0 0 1-.75.75h-1.1a.75.75 0 0 1 0-1.5h1.1a.75.75 0 0 1 .75.75ZM3.35 8a.75.75 0 0 1-.75.75H1.5a.75.75 0 0 1 0-1.5h1.1A.75.75 0 0 1 3.35 8Zm9.07 4.42a.75.75 0 0 1 0 1.06l-.78.78a.75.75 0 1 1-1.06-1.06l.78-.78a.75.75 0 0 1 1.06 0ZM4.42 2.52a.75.75 0 0 1 0 1.06l-.78.78A.75.75 0 1 1 2.58 3.3l.78-.78a.75.75 0 0 1 1.06 0Zm8.16-.78.78.78a.75.75 0 1 1-1.06 1.06l-.78-.78a.75.75 0 0 1 1.06-1.06ZM3.36 11.64l.78.78a.75.75 0 1 1-1.06 1.06l-.78-.78a.75.75 0 0 1 1.06-1.06Z"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="size-full">
                  <path
                    fill="currentColor"
                    d="M13.9 10.32A6.5 6.5 0 0 1 5.68 2.1 6.5 6.5 0 1 0 13.9 10.32Z"
                  />
                </svg>
              )}
            </span>
          </button>
          <div className="relative">
            <button
              ref={settingsBtnRef}
              data-testid="customization-dropdown-trigger"
              className="group cursor-pointer truncate rounded-pill text-sm/sm font-medium active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 border-line active:bg-ink/3 disabled:hover:bg-fill disabled:hover:text-ink/80 disabled:active:bg-fill light:disabled:hover:bg-surface gap-2 p-2 group relative flex size-[1.75rem] shrink-0 items-center justify-center rounded-pill max-mobile:min-h-0 max-mobile:overflow-visible max-mobile:before:absolute max-mobile:before:-inset-[2px] max-mobile:before:content-[''] bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] overflow-hidden text-ink/80 transition-[background-color,color,transform] hover:bg-ink/10 hover:text-ink light:hover:bg-surface border-0"
              id={settingsId}
              aria-label="Settings"
              aria-haspopup="dialog"
              aria-expanded={openMenu === 'settings'}
              data-state={openMenu === 'settings' ? 'open' : 'closed'}
              type="button"
              onClick={() => toggle('settings')}
            >
              <span
                data-icon="true"
                className={`flex shrink-0 items-center justify-center size-4 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${openMenu === 'settings' ? 'rotate-45' : 'group-hover:rotate-45'}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" className="size-full">
                  <path
                    fill="currentColor"
                    d="M7 0C7.27614 0 7.5 0.223858 7.5 0.5V1.52344C8.13999 1.58112 8.74782 1.74881 9.30566 2.00684L9.81738 1.12109C9.95549 0.882102 10.2609 0.800475 10.5 0.938477C10.7388 1.07664 10.8206 1.38208 10.6826 1.62109L10.1709 2.50684C10.6828 2.86878 11.1291 3.31632 11.4912 3.82812L12.3779 3.31738C12.6171 3.17943 12.9225 3.2609 13.0605 3.5C13.1985 3.73898 13.1167 4.04442 12.8779 4.18262L11.9922 4.69238C12.2507 5.25077 12.4188 5.85925 12.4766 6.5H13.5C13.7761 6.5 14 6.72386 14 7C14 7.27614 13.7761 7.5 13.5 7.5H12.4766C12.4188 8.14044 12.2505 8.74848 11.9922 9.30664L12.8779 9.81738C13.1167 9.95558 13.1985 10.261 13.0605 10.5C12.9225 10.7391 12.6171 10.8206 12.3779 10.6826L11.4912 10.1709C11.129 10.683 10.6822 11.1301 10.1699 11.4922L10.6826 12.3799C10.8205 12.619 10.7391 12.9245 10.5 13.0625C10.2611 13.2004 9.95559 13.1185 9.81738 12.8799L9.30566 11.9932C8.74785 12.2512 8.13996 12.4179 7.5 12.4756V13.5C7.5 13.7761 7.27614 14 7 14C6.72386 14 6.5 13.7761 6.5 13.5V12.4756C5.85973 12.4179 5.2514 12.2514 4.69336 11.9932L4.18262 12.8789C4.04456 13.118 3.73914 13.2005 3.5 13.0625C3.26085 12.9244 3.17932 12.6181 3.31738 12.3789L3.8291 11.4922C3.31684 11.1299 2.86904 10.6832 2.50684 10.1709L1.62109 10.6826C1.38201 10.8206 1.07658 10.739 0.938477 10.5C0.800475 10.2609 0.882102 9.95549 1.12109 9.81738L2.00684 9.30566C1.74881 8.74782 1.58112 8.13999 1.52344 7.5H0.5C0.223858 7.5 0 7.27614 0 7C0 6.72386 0.223858 6.5 0.5 6.5H1.52344C1.58115 5.8597 1.7486 5.25142 2.00684 4.69336L1.12109 4.18262C0.882102 4.04451 0.800475 3.73909 0.938477 3.5C1.07658 3.26101 1.38201 3.17938 1.62109 3.31738L2.50684 3.82812C2.86907 3.31594 3.31686 2.86899 3.8291 2.50684L3.31738 1.62109C3.17939 1.38208 3.26115 1.07665 3.5 0.938477C3.73909 0.800444 4.0445 0.882083 4.18262 1.12109L4.69336 2.00586C5.25132 1.74772 5.85984 1.58114 6.5 1.52344V0.5C6.5 0.223858 6.72386 0 7 0ZM5.19629 11.123C5.74857 11.365 6.35844 11.5 7 11.5C8.66238 11.5 10.1124 10.5975 10.8916 9.25684C10.8929 9.25455 10.8942 9.25228 10.8955 9.25C10.8967 9.24797 10.8982 9.24615 10.8994 9.24414C11.2017 8.72 11.402 8.12997 11.4717 7.5H7.28809L5.19629 11.123ZM4.33105 3.37598C3.22025 4.1954 2.5 5.51374 2.5 7C2.5 8.48605 3.22052 9.80359 4.33105 10.623L6.42285 6.99902L4.33105 3.37598ZM7 2.5C6.35858 2.5 5.74848 2.63413 5.19629 2.87598L7.28809 6.5H11.4717C11.402 5.86973 11.202 5.27921 10.8994 4.75488C10.8984 4.75311 10.8965 4.75179 10.8955 4.75C10.8941 4.74748 10.893 4.74473 10.8916 4.74219C10.1123 3.40189 8.66208 2.5 7 2.5Z"
                  ></path>
                </svg>
              </span>
            </button>
            <SettingsModal
              isOpen={openMenu === 'settings'}
              onClose={closeDropdowns}
              settings={settings}
              patchSettings={patchSettings}
            />
          </div>
          <button
            className="group cursor-pointer truncate rounded-pill text-sm/sm font-medium active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 border-line active:bg-ink/3 disabled:hover:bg-fill disabled:hover:text-ink/80 disabled:active:bg-fill light:disabled:hover:bg-surface gap-2 p-2 group relative flex size-[1.75rem] shrink-0 items-center justify-center rounded-pill max-mobile:min-h-0 max-mobile:overflow-visible max-mobile:before:absolute max-mobile:before:-inset-[2px] max-mobile:before:content-[''] bg-fill shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:border-transparent light:bg-surface light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under)] overflow-hidden text-ink/80 transition-[background-color,color,transform] hover:bg-ink/10 hover:text-ink light:hover:bg-surface border-0 max-mobile:hidden"
            aria-label="Link Mobile Device"
            type="button"
            onClick={() =>
              window.alert(
                'Scan the QR from the Lighter mobile app to link this device.'
              )
            }
          >
            <span
              data-icon="true"
              className="flex shrink-0 items-center justify-center size-4 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                fill="currentColor"
                viewBox="0 0 256 256"
                className="size-full"
              >
                <path d="M224,72H208V64a24,24,0,0,0-24-24H40A24,24,0,0,0,16,64v96a24,24,0,0,0,24,24H152v8a24,24,0,0,0,24,24h48a24,24,0,0,0,24-24V96A24,24,0,0,0,224,72ZM40,168a8,8,0,0,1-8-8V64a8,8,0,0,1,8-8H184a8,8,0,0,1,8,8v8H176a24,24,0,0,0-24,24v72Zm192,24a8,8,0,0,1-8,8H176a8,8,0,0,1-8-8V96a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8Zm-96,16a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h40A8,8,0,0,1,136,208Zm80-96a8,8,0,0,1-8,8H192a8,8,0,0,1,0-16h16A8,8,0,0,1,216,112Z"></path>
              </svg>
            </span>
          </button>
          {isConnected && userAddress ? (
            <button
              data-testid="account-button"
              type="button"
              onClick={() => logout()}
              title="Disconnect"
              className="group inline-flex w-fit shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] bg-fill text-ink shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under)] light:bg-surface hover:bg-ink/10 gap-1 p-1 max-mobile:min-h-(--control-h-md) h-[1.75rem] px-3 max-mobile:hidden"
            >
              <span className="font-mono tabular-nums">{truncateAddress(userAddress)}</span>
            </button>
          ) : (
            <button
              data-testid="connect-wallet-button"
              type="button"
              onClick={openConnectModal}
              className="group inline-flex w-fit shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 bg-ink text-bg shadow-border-button-primary hover:bg-ink/90 active:bg-ink/80 disabled:hover:bg-ink disabled:active:bg-ink light:shadow-(--btn-primary-shadow) gap-1 p-1 max-mobile:min-h-(--control-h-md) h-[1.75rem] px-3 max-mobile:hidden"
            >
              <span>Connect Wallet</span>
            </button>
          )}
          <button
            type="button"
            data-testid="connect-wallet-button-mobile"
            aria-label={isConnected ? 'Account' : 'Connect wallet'}
            onClick={isConnected ? () => logout() : openConnectModal}
            className={`hidden max-mobile:flex ${ICON_BTN} border-0`}
          >
            <span
              data-icon="true"
              className="flex size-4 shrink-0 items-center justify-center transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 256 256"
                fill="currentColor"
                className="size-full"
              >
                <path d="M216,72H56a8,8,0,0,1,0-16H192a8,8,0,0,0,0-16H56A24,24,0,0,0,32,64V192a24,24,0,0,0,24,24H216a16,16,0,0,0,16-16V88A16,16,0,0,0,216,72Zm0,128H56a8,8,0,0,1-8-8V91.64A24.19,24.19,0,0,0,56,96H216Zm-20-60a12,12,0,1,1,12-12A12,12,0,0,1,196,140Z" />
              </svg>
            </span>
          </button>
          <button
            type="button"
            data-testid="mobile-menu-trigger"
            aria-label="Open navigation menu"
            aria-expanded={menuOpen}
            onClick={openMobileMenu}
            className={`hidden max-mobile:flex ${ICON_BTN} border-0`}
          >
            <span
              data-icon="true"
              className="flex size-4 shrink-0 items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 256 256"
                fill="currentColor"
                className="size-full"
              >
                <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z" />
              </svg>
            </span>
          </button>
        </div>
      </div>
      <MobileSidebar
        open={menuOpen}
        onClose={closeMenu}
        blockHeight={formattedBlock}
      />
    </div>
  )
}
